import { Map as MapIcon } from '@mui/icons-material';
import { Alert, Box, Paper, Snackbar, Typography } from '@mui/material';
import { AxiosError } from 'axios';
import _ from 'lodash';
import 'mapbox-gl/dist/mapbox-gl.css';
import React, { useContext, useEffect, useRef, useState } from 'react';
import Map, { MapRef, Marker, NavigationControl } from 'react-map-gl/mapbox';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useOptimizedRoutePermissions } from '../../../hooks/useOptimizedRoutePermissiona';
import { useSearchTerm } from '../../../hooks/useSearchTerm';
import { useGetEventMap } from '../../../services/react-query/events';
import { useGetOptimizedEventRoute } from '../../../services/react-query/mapbox/useGetOptimizedEventRoute';
import { Loading } from '../../../uikit';
import { buildOptimizationCoordinates, fitLocationsOnMap, fitRouteGeometryOnMap, useOptimizedEventRouteData } from '../../../utility/map';
import { FilterContext } from '../../layouts/EventsLayout/EventsLayout';
import EventMapItemPopover, { EventMapPopupState } from './EventMapItemPopover';
import EventMapMarker from './EventMapMarker';
import EventOptimizedRouteInfo from './EventMapRoute/EventOptimizedRouteInfo';
import EventOptimizeRouteControl from './EventMapRoute/EventOptimizeRouteControl';
import EventRouteLayer from './EventMapRoute/EventRouteLayer';
import { errorMessageStyles, eventMapContainerStyles, loadingOverlayStyles, mapStyles, mapWrapperStyles } from './eventMapStyles';

const DEFAULT_CENTER = {
  latitude: 20,
  longitude: 0,
  zoom: 1.2,
};

export default function EventMap() {
  const filters = useContext(FilterContext);
  const mapRef = useRef<MapRef>(null);
  const { isMobile } = useIsMobile();
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [optimizedRouteErrorMessage, setOptimizedRouteErrorMessage] = useState<string | null>(null);
  const [popupState, setPopupState] = useState<EventMapPopupState>({
    anchorEl: null,
    mapEvent: null,
  });

  const params = {
    filters,
    search: useSearchTerm(),
  };
  // Load map events using the current filters and search term.
  const { data: eventMapData, isLoading: isLoadingEvents, error: isGetEventMapError, isFetching: isFetchingEvents } = useGetEventMap({ params });
  const mappedEvents = eventMapData?.data ?? [];

  // Handles browser location permissions and optimization toggle state.
  const { userLocationCoords, tooltipTitle, isOptimizeActive, isLoadingLocation, handleOptimizeClick } = useOptimizedRoutePermissions(mappedEvents);

  // Shows the loading overlay before the map is ready.
  const showInitialLoading = isLoadingEvents || !isMapLoaded;

  // Shows a translucent loading overlay while refreshing events.
  const showFetchingOverlay = isFetchingEvents && isMapLoaded;

  const errorMessage = (isGetEventMapError as AxiosError<{ error: string }>)?.response?.data?.error ?? 'Unable to load map events.';

  // Build the Mapbox coordinate string in "lng,lat;lng,lat" format.
  const coordinates = React.useMemo(() => buildOptimizationCoordinates(mappedEvents, userLocationCoords), [mappedEvents, userLocationCoords]);
  // Request the optimized route only when optimization is enabled.
  const {
    data: optimizedRoute,
    isFetching: isFetchingOptimizedRoute,
    error: optimizedRouteError,
  } = useGetOptimizedEventRoute({ params: { coordinates }, enabled: isOptimizeActive });

  useEffect(() => {
    if (!optimizedRouteError) {
      return;
    }
    const error = optimizedRouteError as AxiosError<{ error: string }>;
    setOptimizedRouteErrorMessage(error.response?.data.error ?? 'Unable to optimize route.');
  }, [optimizedRouteError]);

  // Transform the Mapbox response into UI-friendly route data.
  const { trip, geometry, orderedEvents, waypointOrderMap } = useOptimizedEventRouteData(optimizedRoute, mappedEvents);

  // Show optimized numbering only after a valid route is returned.
  const showOptimizedMarkers = isOptimizeActive && !!trip;

  // Route optimization is loading while requesting the user's location
  // or waiting for the optimized route response.
  const isLoadingOptimization = isLoadingLocation || isFetchingOptimizedRoute;

  // Fit events on initial load.
  // Include the user location only while optimization is active.
  useEffect(() => {
    if (!isMapLoaded || !mapRef.current || _.isEmpty(mappedEvents)) {
      return;
    }
    if (isOptimizeActive && geometry) {
      fitRouteGeometryOnMap({ map: mapRef.current, geometry });
    } else {
      fitLocationsOnMap({
        map: mapRef.current,
        mappedEvents,
      });
    }
  }, [mappedEvents, geometry, isOptimizeActive, isMapLoaded]);

  const markers = _.map(mappedEvents, (mapEvent, index) => (
    <Marker
      key={mapEvent.uuid}
      latitude={mapEvent.latitude}
      longitude={mapEvent.longitude}
      anchor="bottom"
      onClick={(e) => {
        e.originalEvent.stopPropagation();

        setPopupState({
          anchorEl: e.target.getElement(),
          mapEvent,
        });
      }}
    >
      {/* <EventMapMarker optimized={showOptimizedMarkers} order={waypointOrderMap[index]} color={mapEvent.location_color} /> */}
      <EventMapMarker optimized={showOptimizedMarkers} order={waypointOrderMap[index]} />
    </Marker>
  ));

  return (
    <Box component={Paper} sx={eventMapContainerStyles}>
      <Box sx={mapWrapperStyles}>
        {!isMobile && (
          <>
            {/* Route optimization toggle */}
            <EventOptimizeRouteControl active={isOptimizeActive} tooltipTitle={tooltipTitle} onClick={handleOptimizeClick} loading={isLoadingOptimization} />

            {/* Route summary and stop order */}
            {isOptimizeActive && trip && <EventOptimizedRouteInfo trip={trip} orderedMapEvents={orderedEvents} />}
          </>
        )}

        {isGetEventMapError ? (
          <Typography variant="h6" color="info" sx={errorMessageStyles}>
            <MapIcon fontSize="large" />
            {errorMessage}
          </Typography>
        ) : (
          <>
            {(showInitialLoading || showFetchingOverlay) && (
              <Box sx={loadingOverlayStyles(showFetchingOverlay)}>
                <Loading />
              </Box>
            )}

            {/* Interactive Mapbox map */}
            <Map
              ref={mapRef}
              initialViewState={DEFAULT_CENTER}
              mapboxAccessToken={process.env.REACT_APP_MAPBOX_PUBLIC_TOKEN}
              mapStyle="mapbox://styles/mapbox/navigation-day-v1"
              onLoad={() => setIsMapLoaded(true)}
              style={mapStyles}
            >
              {/* Zoom and compass controls */}
              <NavigationControl position="top-right" />

              {/* Optimized route polyline */}
              {isOptimizeActive && geometry && <EventRouteLayer geometry={geometry} />}

              {/* Event markers */}
              {markers}
            </Map>

            {/* Selected event details popup */}
            <EventMapItemPopover
              popupState={popupState}
              onClose={() =>
                setPopupState({
                  anchorEl: null,
                  mapEvent: null,
                })
              }
            />

            {/* Optimized route error message */}
            {optimizedRouteErrorMessage && (
              <Snackbar open={!!optimizedRouteErrorMessage} autoHideDuration={6000} onClose={() => setOptimizedRouteErrorMessage(null)}>
                <Alert severity="error" onClose={() => setOptimizedRouteErrorMessage(null)}>
                  {optimizedRouteErrorMessage}
                </Alert>
              </Snackbar>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
